import React, { useEffect } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ChevronDown, ChevronUp, CheckIcon } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';
import { useApplicationStore } from 'src/store/application';
import { useCompareStore } from 'src/store/compare';
import { InsurancePlan, MedicalPlan } from 'src/services/interfaces/insurance';
import { useInsuranceStore } from 'src/store/insurance';
import { FormPlanDetails } from '@features/onboarding/components/shared/form-plan/FormPlanDetals';
import { ViewMode } from '@features/onboarding/components/shared/form-plan/MedicalPlansControls';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { useTouchDevice } from '@features/onboarding/hooks/useTouchDevice';
import { calculateCoverageLevel } from '@features/onboarding/utils/coverageLevel';
import {
  getCriticalIllnessPriceId,
  PriceSearchConditions,
  getAvailableDependentBenefits,
} from '@features/onboarding/utils/criticalIllnessPriceId';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { calculateAge } from '@features/onboarding/utils/calculateAge';

import CompareIcon from '@components/shared/icon/CompareIcon';
import { cn, isLollySkin } from '@utils/index';
import { CriticalIllnessCoverageSelector } from './CriticalIllnessCoverageSelector';
import { ListPlanCard } from './ListPlanCard';
import { getPlanStats as getPlanStatsUtil } from './utils';

interface FormPlanListProps<T extends FieldValues> {
  plans: MedicalPlan[];
  form: UseFormReturn<T>;
  name: Path<T>;
  viewMode: ViewMode;
  planLength: number;
  hideRadio?: boolean;
  hidePrices?: boolean;
  expandedPlanIds: string[];
  onToggleExpand: (planId: string) => void;
  onOpenCompare?: () => void;
  planType?: string;
}

export const FormPlanList = <T extends FieldValues>({
  plans,
  form,
  name,
  viewMode,
  planLength,
  hideRadio,
  hidePrices,
  expandedPlanIds,
  onOpenCompare,
  onToggleExpand,
  planType = 'summary',
}: FormPlanListProps<T>) => {
  const allPlans = useInsuranceStore((state) => state.plans) as InsurancePlan[];
  const providersFromStore = useInsuranceStore((state) => state.providers);
  const { setData } = useOnboardingStore();
  const aiHighlightedPlanUid = useOnboardingStore(
    (s) => s.aiHighlightedPlanUid
  );
  const setAiHighlightedPlan = useOnboardingStore(
    (s) => s.setAiHighlightedPlan
  );
  const { getPlanPrice } = usePlan();
  const {
    activeCategories,
    isMainPlanRejected,
    activeSubscriptionPlans,
    tier,
    acceptedTier,
    signedAt,
  } = useApplicationStore();

  const hasAnyActiveExtraPlan = activeCategories.some(
    (cat) => cat !== 'Major Medical'
  );

  const isPlanBlocked = (plan: MedicalPlan): boolean => {
    const fullPlan = allPlans.find((p) => p.uid === plan.id);
    if (!fullPlan) return false;

    if (fullPlan.category === 'Major Medical') {
      return Boolean(isMainPlanRejected);
    }

    return hasAnyActiveExtraPlan;
  };

  const getPlanInfo = (plan: InsurancePlan, planUid: string) => {
    let coverageLevel: number;
    if (plan.category === 'Major Medical') {
      const medicalTags =
        (form.watch('medicalTags' as Path<T>) as unknown as string[]) || [];
      coverageLevel = calculateCoverageLevel(medicalTags);
    } else {
      const planTags =
        (form.watch('planTags' as Path<T>) as unknown as Record<
          string,
          string[]
        >) || {};
      const planTagsForThisPlan = planTags[planUid] || [];
      coverageLevel = calculateCoverageLevel(planTagsForThisPlan);
    }

    const selectedTier = acceptedTier?.toString() || undefined;
    let selectedCoverageAmount: number | undefined;

    if (plan.category === 'Major Medical') {
      // if (form.watch('medicalPlanTier' as Path<T>)) {
      //   selectedTier = form.watch(
      //     'medicalPlanTier' as Path<T>
      //   ) as unknown as string;
      // }
      if (form.watch('medicalPlanCoverageAmount' as Path<T>)) {
        selectedCoverageAmount = form.watch(
          'medicalPlanCoverageAmount' as Path<T>
        ) as unknown as number;
      }
    } else if (plan.category === 'Dental') {
      // if (form.watch('dentalPlanTier' as Path<T>)) {
      //   selectedTier = form.watch(
      //     'dentalPlanTier' as Path<T>
      //   ) as unknown as string;
      // }
      if (form.watch('dentalPlanCoverageAmount' as Path<T>)) {
        selectedCoverageAmount = form.watch(
          'dentalPlanCoverageAmount' as Path<T>
        ) as unknown as number;
      }
    } else if (plan.category === 'Critical Illness') {
      const sanitizeCategory = (category: string) =>
        category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

      const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
      const coverageAmountKey = `${criticalIllnessPlanKey.replace(
        'Plan',
        'PlanCoverageAmount'
      )}`;

      const coverageAmount = form.watch(
        coverageAmountKey as Path<T>
      ) as unknown as number | undefined;

      selectedCoverageAmount = coverageAmount ?? 0;

      // const selectedPlanUid = form.watch(name as Path<T>) as unknown as
      //   | string
      //   | undefined;

      // if (selectedPlanUid === planUid) {
      //   const storeData = useOnboardingStore.getState().data;
      //   const storeCoverageAmount = (storeData as Record<string, unknown>)[
      //     coverageAmountKey
      //   ] as number | undefined;
      //   if (storeCoverageAmount) {
      //     selectedCoverageAmount = storeCoverageAmount;
      //   }
      // } else {
      //   selectedCoverageAmount = 10000;
      // }
    }

    if (plan.category === 'Dental') {
      if (form.watch('dentalPlanCoverageAmount' as Path<T>)) {
        selectedCoverageAmount = form.watch(
          'dentalPlanCoverageAmount' as Path<T>
        ) as unknown as number;
      }
    }

    /*
    if (plan.category === 'Critical Illness') {
      const sanitizeCategory = (category: string) =>
        category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
      const coverageAmountKey = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
      const coverageAmount = form.watch(
        coverageAmountKey as Path<T>
      ) as unknown as number | undefined;
      if (coverageAmount) {
        selectedCoverageAmount = coverageAmount;
      } else {
        selectedCoverageAmount = 10000;
      }
      const selectedPlanUid = form.watch(name as Path<T>) as unknown as
        | string
        | undefined;
      if (selectedPlanUid === planUid) {
        const storeData = useOnboardingStore.getState().data;
        const storeCoverageAmount = (storeData as Record<string, unknown>)[
          coverageAmountKey
        ] as number | undefined;
        if (storeCoverageAmount) {
          selectedCoverageAmount = storeCoverageAmount;
        }
      } else {
        selectedCoverageAmount = 10000;
      }
    }
    */

    const price = getPlanPrice(
      plan,
      coverageLevel,
      selectedTier,
      selectedCoverageAmount
    );

    const result = {
      coverageLevel,
      selectedTier,
      selectedCoverageAmount,
      price,
    };

    return result;
  };

  const getDisplayTier = React.useCallback(
    (planUid: string): string | null => {
      const tierFieldName = name.replace('Plan', 'PlanTier');
      const currentTier = form.watch(tierFieldName as Path<T>) as unknown as
        | string
        | undefined;
      if (currentTier) return currentTier;
      const planObj = allPlans.find((p) => p.uid === planUid);
      if (!planObj) return null;
      return planObj.category === 'Major Medical'
        ? acceptedTier?.toString() || null
        : null;
    },
    [form, name, allPlans, acceptedTier]
  );

  const selectedPlan = form.watch(name);

  const sanitizeCategory = (category: string) =>
    category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const criticalIllnessPlanKey = `${sanitizeCategory('Critical Illness')}Plan`;

  const isTouchDevice = useTouchDevice();
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    window.innerWidth < 1400
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1400);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const comparisonLimit = isTouchDevice || isSmallScreen ? 2 : 3;

  const truncate = (text: string, maxChars: number) => {
    return text.length > maxChars ? text.slice(0, maxChars) + '...' : text;
  };

  const getCIAges = () => {
    let primaryAge = 30;
    let spouseAge: number | undefined;
    const applicants = (form.watch('applicants' as Path<T>) as any[]) || [];
    if (applicants.length > 0) {
      const primaryApplicant = applicants.find(
        (app: any) => app.applicant === 'primary'
      );
      if (primaryApplicant?.dateOfBirth) {
        primaryAge = calculateAge(primaryApplicant.dateOfBirth) ?? 30;
      }
      const spouseApplicant = applicants.find(
        (app: any) => app.applicant === 'spouse'
      );
      if (spouseApplicant?.dateOfBirth) {
        spouseAge = calculateAge(spouseApplicant.dateOfBirth) ?? undefined;
      }
    }
    return { primaryAge, spouseAge };
  };

  const getCIPlanTagsInfo = (planUid: string) => {
    const planTagsObj =
      (form.watch('planTags' as Path<T>) as unknown as Record<
        string,
        string[]
      >) || {};
    const planTagsForThisPlan = planTagsObj[planUid] || [];
    const dependentsCount = planTagsForThisPlan.filter((tag: string) =>
      tag.includes('dependent')
    ).length;
    const hasSpouse = planTagsForThisPlan.some((tag: string) =>
      tag.includes('spouse')
    );
    return { dependentsCount, hasSpouse };
  };

  const handleSelectPlan = (id: string) => {
    const selected = plans.find((plan) => plan.id === id);
    if (!selected) return;

    if (isPlanBlocked(selected)) return;

    const fieldName = name.replace('Plan', 'PlanPrice');
    const tierFieldName = name.replace('Plan', 'PlanTier');
    const coverageAmountFieldName = name.replace('Plan', 'PlanCoverageAmount');
    const dependentBenefitFieldName = name.replace('Plan', 'PlanDependentBenefit');

    if (selectedPlan === id) {
      const resetData: Record<string, unknown> = {
        [name]: '',
        [fieldName]: 0,
        [tierFieldName]: '',
      };

      const plan = allPlans.find((p) => p.uid === id);
      if (plan?.category === 'Critical Illness') {
        resetData[coverageAmountFieldName] = 0;
        resetData[dependentBenefitFieldName] = 5000;
        form.setValue(
          coverageAmountFieldName as Path<T>,
          0 as unknown as T[Path<T>]
        );
        form.setValue(
          dependentBenefitFieldName as Path<T>,
          5000 as unknown as T[Path<T>]
        );

        resetData[fieldName] = 0;
        form.setValue(
          fieldName as Path<T>,
          0 as unknown as T[Path<T>]
        );
      }

      form.setValue(name as Path<T>, '' as unknown as T[Path<T>]);
      setAiHighlightedPlan(null);
      setData(resetData);
    } else {
      console.log('nameid', name, id);
      form.setValue(name as Path<T>, id as unknown as T[Path<T>]);
      const fullPlan = allPlans.find((p) => p.uid === id);
      const selectedTier: string | null = acceptedTier?.toString() || null;
      let selectedCoverageAmount: number | null = null;
      let selectedDependentBenefit: number | null = null;
      let priceNumber = 0;

      if (fullPlan?.category === 'Critical Illness') {
        const currentCoverage = form.getValues(
          coverageAmountFieldName as Path<T>
        ) as unknown as number | null;
        selectedCoverageAmount =
          currentCoverage && currentCoverage > 0
            ? currentCoverage
            : 10000;

        const currentDepBenefit = form.getValues(
          dependentBenefitFieldName as Path<T>
        ) as unknown as number | null;
        selectedDependentBenefit =
          currentDepBenefit && currentDepBenefit !== 0 ? currentDepBenefit : 5000;

        // Validate dependent benefit against main coverage
        const availableDepBenefits = getAvailableDependentBenefits(selectedCoverageAmount);
        if (!availableDepBenefits.includes(selectedDependentBenefit)) {
          selectedDependentBenefit = 5000;
        }

        const { primaryAge, spouseAge } = getCIAges();
        const { dependentsCount, hasSpouse } = getCIPlanTagsInfo(fullPlan.uid);

        const conditions: PriceSearchConditions = {
          coverageAmount: selectedCoverageAmount,
          primaryAge,
          spouseAge,
          dependentsCount,
          hasSpouse,
          dependentBenefit: dependentsCount > 0 ? selectedDependentBenefit : 0,
        };

        const priceId = getCriticalIllnessPriceId(fullPlan, conditions);
        const validPrice = fullPlan.prices.find(
          (price) => price.id === priceId
        );
        priceNumber = validPrice?.price || 0;
      } else {
        const priceObj = fullPlan?.prices.find(
          (p) =>
            !acceptedTier?.toString() || p.tier === acceptedTier?.toString()
        );
        priceNumber = priceObj?.price || 0;
        console.log('priceObj', priceObj);
      }

      const updateData: Record<string, unknown> = {
        [name]: id,
        [fieldName]: priceNumber,
        [tierFieldName]: selectedTier,
      };

      if (selectedCoverageAmount !== null) {
        updateData[coverageAmountFieldName] = selectedCoverageAmount;
        form.setValue(
          coverageAmountFieldName as Path<T>,
          selectedCoverageAmount as unknown as T[Path<T>]
        );
      }

      if (selectedDependentBenefit !== null) {
        updateData[dependentBenefitFieldName] = selectedDependentBenefit;
        form.setValue(
          dependentBenefitFieldName as Path<T>,
          selectedDependentBenefit as unknown as T[Path<T>]
        );
      }

      console.log('updateData', updateData);

      setAiHighlightedPlan(null);
      setData(updateData);
    }
  };

  React.useEffect(() => {
    const trySelectPlan = (planUid: string) => {
      if (selectedPlan === planUid) return;
      
      const planToSelect = plans.find(p => p.id === planUid);
      if (!planToSelect) return;
      
      const fullPlan = allPlans.find((p) => p.uid === planUid);
      if (!fullPlan) return;
      
      const isBlocked = fullPlan.category === 'Major Medical'
        ? Boolean(isMainPlanRejected)
        : hasAnyActiveExtraPlan;
      
      if (!isBlocked) {
        handleSelectPlan(planUid);
      }
    };

    const handleAISelectPlan = (event: CustomEvent) => {
      const { planUid, fieldName: eventFieldName } = event.detail;
      if (eventFieldName === name && planUid) {
        trySelectPlan(planUid);
      }
    };

    const handleAIDeselectPlan = (event: CustomEvent) => {
      const { fieldName: eventFieldName, planUid } = event.detail;
      if (eventFieldName === name) {
        form.setValue(name as Path<T>, '' as unknown as T[Path<T>]);

        const fieldName = name.replace('Plan', 'PlanPrice');
        const tierFieldName = name.replace('Plan', 'PlanTier');
        const coverageAmountFieldName = name.replace('Plan', 'PlanCoverageAmount');
        const dependentBenefitFieldName = name.replace('Plan', 'PlanDependentBenefit');

        form.setValue(fieldName as Path<T>, 0 as unknown as T[Path<T>]);
        form.setValue(tierFieldName as Path<T>, '' as unknown as T[Path<T>]);

        const plan = planUid ? allPlans.find((p) => p.uid === planUid) : null;
        if (plan?.category === 'Critical Illness') {
          form.setValue(
            coverageAmountFieldName as Path<T>,
            10000 as unknown as T[Path<T>]
          );
          form.setValue(
            dependentBenefitFieldName as Path<T>,
            5000 as unknown as T[Path<T>]
          );
        } else {
          form.setValue(
            coverageAmountFieldName as Path<T>,
            null as unknown as T[Path<T>]
          );
        }
      }
    };

    const checkStoreData = () => {
      if (plans.length === 0) return;
      
      const storeData = useOnboardingStore.getState().data;
      const selectedPlanUid = (storeData as any)[name];
      const aiHighlightedUid = useOnboardingStore.getState().aiHighlightedPlanUid;
      
      if (aiHighlightedUid && selectedPlanUid === aiHighlightedUid && selectedPlan !== aiHighlightedUid) {
        trySelectPlan(aiHighlightedUid);
      }
    };

    window.addEventListener('ai-select-plan', handleAISelectPlan as EventListener);
    window.addEventListener('ai-deselect-plan', handleAIDeselectPlan as EventListener);
    const timeoutId = setTimeout(checkStoreData, 1500);
    
    return () => {
      window.removeEventListener('ai-select-plan', handleAISelectPlan as EventListener);
      window.removeEventListener('ai-deselect-plan', handleAIDeselectPlan as EventListener);
      clearTimeout(timeoutId);
    };
  }, [name, plans, allPlans, isMainPlanRejected, activeCategories, selectedPlan, handleSelectPlan]);

  const [expandedDescriptions, setExpandedDescriptions] = React.useState<
    string[]
  >([]);
  const toggleDescription = (id: string) => {
    setExpandedDescriptions((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };
  const isDescriptionExpanded = (id: string) =>
    expandedDescriptions.includes(id);

  const { togglePlan, selectedPlans } = useCompareStore();

  const handleSelectCompare = (id: string) => {
    togglePlan(id);
  };

  const getFormattedPrice = (plan: MedicalPlan, allPlans: InsurancePlan[]) => {
    const fullPlan = allPlans.find((p) => p.uid === plan.id);
    if (!fullPlan) {
      return plan.price;
    }

    if (fullPlan.category === 'Critical Illness') {
      const sanitizeCategory = (category: string) =>
        category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const coverageAmountKey = `${sanitizeCategory(fullPlan.category)}PlanCoverageAmount`;
      const formCoverage = form.watch(coverageAmountKey as Path<T>) as unknown as number | undefined;
      const storeCoverage = (useOnboardingStore.getState().data as Record<string, unknown>)[coverageAmountKey] as number | undefined;
      const coverageAmount = formCoverage || storeCoverage || 0;
      if (coverageAmount === 0) {
        return '$0.00';
      }
      const planInfo = getPlanInfo(fullPlan, plan.id);
      const formattedPrice = `$${planInfo.price.toFixed(2)}`;
      return formattedPrice;
    }

    const tier = getDisplayTier(plan.id);

    let coverageLevel = 1;
    if (fullPlan.category === 'Major Medical') {
      const medicalTags =
        (form.watch('medicalTags' as Path<T>) as unknown as string[]) || [];
      coverageLevel = calculateCoverageLevel(medicalTags);
    } else {
      const planTagsObj =
        (form.watch('planTags' as Path<T>) as unknown as Record<
          string,
          string[]
        >) || {};
      const tags = planTagsObj[plan.id] || [];
      coverageLevel = calculateCoverageLevel(tags);
    }

    if (tier) {
      const entry = fullPlan.prices?.find(
        (e) => e.tier === tier && e.coverage === coverageLevel
      );
      if (entry) {
        return `$${entry.price.toFixed(2)}`;
      }
    }

    const planInfo = getPlanInfo(fullPlan, plan.id);
    const formattedPrice = `$${planInfo.price.toFixed(2)}`;
    return formattedPrice;
  };

  const getPlanStats = (
    fullPlan?: InsurancePlan
  ): Array<{ label: string; value: string }> => {
    if (!fullPlan) return [];

    const getCriticalIllnessMaxBenefit = () => {
      const sanitizeCategory = (category: string) =>
        category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const criticalIllnessPlanKey = `${sanitizeCategory('Critical Illness')}Plan`;
      const criticalIllnessCoverageAmountKey = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
      return (
        (form.getValues?.(criticalIllnessCoverageAmountKey as any) ??
          (form.watch?.(criticalIllnessCoverageAmountKey as any) as any)) ||
        10000
      );
    };

    return getPlanStatsUtil(fullPlan, getCriticalIllnessMaxBenefit);
  };

  // useEffect(() => {
  //   if (aiHighlightedPlanUid) {
  //     onToggleExpand(aiHighlightedPlanUid);
  //   }
  // }, [aiHighlightedPlanUid]);

  if (viewMode === 'tab') {
    return (
      <div className="overflow-x-auto mt-6.5">
        <table className="w-full border-collapse text-[14px] md:text-[14px]">
          <thead className="text-left text-black border-b text-[12px]">
            <tr>
              {!hideRadio && <th className="py-2"></th>}
              <th className="py-2 max-lg:hidden">Insurer</th>
              <th className="py-2">Plan Name</th>
              <th className="py-2 max-md:hidden">Description</th>
              {!hidePrices && <th className="py-2">Monthly Premium</th>}
              <th className="py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, index) => {
              const isSelected = selectedPlan === plan.id;
              const isCompare = selectedPlans.includes(plan.id);
              const uniqueKey = `${planType}-${plan.id}-${index}`;
              const isComparisonLimitReached =
                selectedPlans.length >= comparisonLimit && !isCompare;

              const fullPlan = allPlans.find((p) => p.uid === plan.id);
              const blocked = isPlanBlocked(plan);
              const planCategory = fullPlan?.category;
              
              const providerName = fullPlan?.providerId
                ? providersFromStore.find((pr) => {
                    return (
                      pr.uid === String(fullPlan.providerId) ||
                      (pr as any).id === fullPlan.providerId
                    );
                  })?.name || fullPlan.category
                : fullPlan?.category;

              return (
                <React.Fragment key={uniqueKey}>
                  <tr className="border-b text-[12px] md:text-[12px] align-center">
                    {!hideRadio && (
                      <td className="py-3 pl-2 pr-1">
                        <button
                          type="button"
                          onClick={() => handleSelectPlan(plan.id)}
                          className={cn(
                            'px-3 py-1 rounded-full text-xs font-semibold cursor-pointer',
                            isSelected
                              ? 'bg-primary text-white'
                              : 'bg-view-section text-black hover:bg-[#e4e2db]'
                          )}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                      </td>
                    )}
                    <td className="py-3 max-lg:hidden">{providerName}</td>
                    <td className="py-3">{plan.title}</td>
                    <td className="py-3 max-md:hidden">
                      {truncate(plan.description, 31)}
                    </td>
                    {!hidePrices && (
                      <td className="py-3">
                        {getFormattedPrice(plan, allPlans)}
                      </td>
                    )}
                    <td className="py-3 pr-2">
                      <div className="flex items-center align-center gap-3">
                        {planLength > 1 && !hideRadio && (
                          <button
                            title="Compare plan"
                            type="button"
                            onClick={() => handleSelectCompare(plan.id)}
                            disabled={isComparisonLimitReached}
                            className={`text-sm align-center cursor-pointer hover:text-red-800 ${
                              isCompare
                                ? 'text-red-600 font-semibold'
                                : 'text-black'
                            } ${isComparisonLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <CompareIcon />
                          </button>
                        )}
                        <button
                          type="button"
                          title={
                            expandedPlanIds.includes(plan.id)
                              ? 'Hide details'
                              : 'Show details'
                          }
                          onClick={() => onToggleExpand(plan.id)}
                          className="ml-auto p-0 text-black cursor-pointer hover:text-red-800"
                          aria-label={
                            expandedPlanIds.includes(plan.id)
                              ? 'Hide details'
                              : 'Show details'
                          }
                        >
                          <div className="flex flex-col gap-1">
                            <span className="w-4 h-0.5 bg-primary rounded-lg" />
                            <span className="w-4 h-0.5 bg-primary rounded-lg" />
                            <span className="w-4 h-0.5 bg-primary rounded-lg" />
                          </div>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {allPlans.find((p) => p.uid === plan.id)?.category ===
                    'Critical Illness' && (
                    <tr className="bg-white">
                      <td colSpan={hideRadio ? 5 : 6}>
                        <CriticalIllnessCoverageSelector
                          planId={plan.id}
                          plan={
                            allPlans.find(
                              (p) => p.uid === plan.id
                            ) as InsurancePlan
                          }
                          form={form}
                          name={name}
                          viewMode={viewMode}
                          disabled={isPlanBlocked(plan)}
                        />
                      </td>
                    </tr>
                  )}
                  {expandedPlanIds.includes(plan.id) && (
                    <tr className="bg-white">
                      <td
                        colSpan={hideRadio ? 5 : 6}
                        className="p-4 border-b border-gray-200"
                      >
                        <FormPlanDetails
                          plan={
                            allPlans.find(
                              (e) => e.uid === plan.id
                            ) as InsurancePlan
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4 relative',
        viewMode === 'grid' ? 'md:grid md:grid-cols-2 items-start' : ''
      )}
    >
        {plans.map((plan, index) => {
        const isSelected = selectedPlan === plan.id;
        const isCompare = selectedPlans.includes(plan.id);
        const uniqueKey = `${planType}-${plan.id}-${index}-article`;
        const isComparisonLimitReached =
          selectedPlans.length >= comparisonLimit && !isCompare;
        const isLast = index === plans.length - 1;

        const fullPlan = allPlans.find((p) => p.uid === plan.id);
        const formattedPrice = getFormattedPrice(plan, allPlans);

        if (viewMode === 'grid') {
          const planStats = getPlanStats(fullPlan);
          const blocked = isPlanBlocked(plan);
          const planCategory = fullPlan?.category;

          return (
            <article
              key={uniqueKey}
              className={cn(
                'bg-white flex flex-col rounded-2xl overflow-hidden border border-[#ece5d8] p-3 transition-all shadow-[0px_8px_30px_rgba(15,15,15,0.05)]',
                'mt-4',
                isLast && 'mb-2',
                !hideRadio && isSelected
                  ? 'border-primary plan-selected-shadow'
                  : aiHighlightedPlanUid === plan.id
                    ? 'border-[rgba(113,0,54,.75)]'
                    : 'border-transparent shadow'
              )}
            >
              <header
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('footer')) {
                    return;
                  }
                  if (!hideRadio) {
                    handleSelectPlan(plan.id);
                  }
                }}
                className={cn(
                  'flex flex-col gap-4 py-1 md:py-2',
                  !hideRadio && 'cursor-pointer'
                )}
              >
                <div
                  className={cn(
                    'flex justify-between gap-2',
                    !hideRadio || planLength > 1 ? '' : 'justify-start'
                  )}
                >
                  <div className="flex flex-row items-start gap-3 pr-2 flex-1 min-w-0">
                    <img
                      src={plan.image}
                      alt={plan.title}
                      className="xl:size-18 size-16 object-contain shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <h4
                        className={cn(
                          'text-[16px] max-4xl:text-xs font-bold leading-[22px] max-4xl:leading-4 mb-1.5 text-neutral-900'
                        )}
                      >
                        {plan.title}
                      </h4>
                      <p
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDescription(plan.id);
                        }}
                        className={cn(
                          'text-xs leading-none max-2.5xl:text-[10px] font-normal text-[#4a473f] cursor-pointer transition-all duration-200',
                          !isDescriptionExpanded(plan.id) && 'line-clamp-2'
                        )}
                        title="Click to expand"
                      >
                        {plan.description}
                      </p>
                    </div>
                  </div>
                  {!hideRadio || (planLength > 1 && !hideRadio) ? (
                    <div className="flex flex-col items-end gap-2 shrink-0 border-l pl-2">
                      {!hideRadio && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPlan(plan.id);
                          }}
                          className={cn(
                            'px-3 py-1 rounded-sm border border-primary mb-3 text-xs font-semibold cursor-pointer transition-all hover:bg-primary/90 shrink-0',
                            isSelected
                              ? 'bg-primary text-white'
                              : 'bg-white text-primary hover:text-white'
                          )}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                      )}
                      {planLength > 1 && !hideRadio && (
                        <button
                          type="button"
                          title="Compare plan"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenCompare && planLength === 2) {
                              onOpenCompare();
                              return;
                            }
                            if (isComparisonLimitReached && !isCompare) return;
                            handleSelectCompare(plan.id);
                          }}
                          disabled={isComparisonLimitReached && !isCompare}
                          className={cn(
                            'flex items-center gap-2 border-t pt-4 text-[13px] font-semibold shrink-0',
                            isCompare ? 'text-destructive' : 'text-[#3b3a34]',
                            isComparisonLimitReached && !isCompare
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer'
                          )}
                        >
                          <span
                            className={cn(
                              'size-4 rounded border flex items-center justify-center shrink-0',
                              isCompare
                                ? 'bg-destructive border-destructive text-white'
                                : 'border-[#c9c4b6] text-transparent'
                            )}
                          >
                            <CheckIcon className="size-3" />
                          </span>
                          <span className="text-xs">Compare</span>
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-[#e3dbcc] pt-3">
                  {!hidePrices && (
                    <div className="flex flex-col items-center justify-center text-center gap-1">
                      <span className="text-[22px] leading-7 font-bold text-black">
                        {formattedPrice}
                      </span>
                      <span className="text-[11px] leading-3.5 font-medium text-profile-text-muted tracking-[0.02em]">
                        Monthly premium
                      </span>
                    </div>
                  )}
                  {planStats.length > 0 && (
                    <div className="flex-1 flex flex-col gap-2 px-3 border-l border-[#efe8dc]">
                      <dl className="flex flex-col h-fit text-[11px] max-xl:gap-x-4 max-xl:flex-wrap max-xl:flex-row max-xl:gap-y-0 tracking-[0.02em] text-profile-text-muted">
                        {planStats.map(({ label, value }) => {
                          const minWidthClass =
                            value.length > 7 ? 'xl:min-w-29' : 'xl:min-w-10';
                          return (
                            <div
                              key={label}
                              className="flex justify-between max-5xl:flex-col max-2xl:flex-row items-center max-5xl:items-start max-2xl:items-center max-5xl:gap-0 max-2xl:gap-2 text-right max-5xl:text-left max-2xl:text-right gap-2 max-xl:shrink-0 max-xl:whitespace-nowrap"
                            >
                              <dt className="text-[11px] max-5xl:text-left max-2xl:text-right max-2.5xl:text-[10px] font-medium text-[#b7b1a5] text-right xl:min-w-30 max-xl:whitespace-nowrap">
                                {label}
                              </dt>
                              <dd
                                className={cn(
                                  'text-[11px] w-1/2 max-xl:w-fit max-md:text-xs font-semibold text-black text-left max-xl:whitespace-nowrap',
                                  minWidthClass
                                )}
                              >
                                {value}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  )}
                </div>
              </header>
              {(() => {
                const isCriticalIllness =
                  fullPlan?.category === 'Critical Illness';
                const shouldRenderCoverageSelector =
                  isCriticalIllness &&
                  (form.watch?.(name as any) === plan.id || true);

                return shouldRenderCoverageSelector &&
                  fullPlan?.category === 'Critical Illness' ? (
                  <CriticalIllnessCoverageSelector
                    planId={plan.id}
                    plan={fullPlan as InsurancePlan}
                    form={form}
                    name={name}
                    viewMode={viewMode}
                    disabled={isPlanBlocked(plan)}
                  />
                ) : null;
              })()}
              <footer
                className="text-black mt-4"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Accordion
                  type="multiple"
                  value={expandedPlanIds.includes(plan.id) ? ['item-1'] : []}
                  onValueChange={(value) => {
                    const shouldBeOpen = value.includes('item-1');
                    const isCurrentlyOpen = expandedPlanIds.includes(plan.id);
                    if (shouldBeOpen !== isCurrentlyOpen) {
                      onToggleExpand(plan.id);
                    }
                  }}
                >
                  <AccordionItem
                    value="item-1"
                    className="border-t border-[#ede6da] flex flex-col"
                  >
                    <AccordionTrigger
                      className=
                        "flex justify-between items-center w-full text-[13px] pt-3 pb-0 font-semibold p-0 text-[#3b3a34] cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <span>
                        {expandedPlanIds.includes(plan.id)
                          ? 'Hide Plan Details'
                          : 'Show Plan Details'}
                      </span>
                      {expandedPlanIds.includes(plan.id) ? (
                        <ChevronUp className="size-4" />
                      ) : (
                        <ChevronDown className="size-4" />
                      )}
                    </AccordionTrigger>
                    <AccordionContent
                      className="bg-white text-black pt-4"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <FormPlanDetails
                        plan={
                          allPlans.find(
                            (planItem) => planItem.uid === plan.id
                          ) as InsurancePlan
                        }
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </footer>
            </article>
          );
        }

        return (
          <ListPlanCard
            key={uniqueKey}
            plan={plan}
            fullPlan={fullPlan as InsurancePlan}
            form={form}
            name={name}
            viewMode={viewMode}
            planLength={planLength}
            hideRadio={hideRadio}
            hidePrices={hidePrices}
            isSelected={isSelected}
            isCompare={isCompare}
            isComparisonLimitReached={isComparisonLimitReached}
            aiHighlightedPlanUid={aiHighlightedPlanUid}
            formattedPrice={formattedPrice}
            isPlanBlocked={isPlanBlocked}
            expandedPlanIds={expandedPlanIds}
            onToggleExpand={onToggleExpand}
            onSelectPlan={handleSelectPlan}
            onToggleCompare={handleSelectCompare}
            onOpenCompare={onOpenCompare}
            toggleDescription={toggleDescription}
            isDescriptionExpanded={isDescriptionExpanded}
            allPlans={allPlans}
            isLast={isLast}
          />
        );
      })}
    </div>
  );
};
